import React, { useState, useRef, useEffect } from "react";
import {
  Autocomplete,
  Avatar,
  Box,
  Dialog,
  TextField,
  CircularProgress,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import imageCompression from 'browser-image-compression';
import { useSelector } from "react-redux";
import {
  getCoutries,
  getDistrictsBaseOnProvince,
  getProvincesBaseOnCountry,
} from "../../../../services/public_service.js";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Camera } from "lucide-react";
import { toast } from "react-toastify";
import { createDownlineAgent } from "../../../../services/agent_management_service.js";
import CreateAgentErrorModal from "./ErrorModal.jsx";
import CreateAgentSuccessModal from "./SuccessModal.jsx";
import redFocusStyles from "../../../../components/ui/formUI/RedFocusedStyleV2.jsx";

const countryDialCodes = {
  AF: { code: "+93", length: 9, name: "Afghanistan" },
  AL: { code: "+355", length: 9, name: "Albania" },
  DZ: { code: "+213", length: 9, name: "Algeria" },
  AD: { code: "+376", length: 6, name: "Andorra" },
  AO: { code: "+244", length: 9, name: "Angola" },
  AG: { code: "+1", length: 10, name: "Antigua and Barbuda" },
  AR: { code: "+54", length: 10, name: "Argentina" },
  AM: { code: "+374", length: 8, name: "Armenia" },
  AU: { code: "+61", length: 9, name: "Australia" },
  AT: { code: "+43", length: 10, name: "Austria" },
  AZ: { code: "+994", length: 9, name: "Azerbaijan" },
  BS: { code: "+1", length: 10, name: "Bahamas" },
  BH: { code: "+973", length: 8, name: "Bahrain" },
  BD: { code: "+880", length: 10, name: "Bangladesh" },
  BB: { code: "+1", length: 10, name: "Barbados" },
  BY: { code: "+375", length: 9, name: "Belarus" },
  BE: { code: "+32", length: 9, name: "Belgium" },
  BZ: { code: "+501", length: 7, name: "Belize" },
  BJ: { code: "+229", length: 8, name: "Benin" },
  BT: { code: "+975", length: 8, name: "Bhutan" },
  BO: { code: "+591", length: 8, name: "Bolivia" },
  BA: { code: "+387", length: 8, name: "Bosnia and Herzegovina" },
  BW: { code: "+267", length: 7, name: "Botswana" },
  BR: { code: "+55", length: 11, name: "Brazil" },
  BN: { code: "+673", length: 7, name: "Brunei" },
  BG: { code: "+359", length: 9, name: "Bulgaria" },
  BF: { code: "+226", length: 8, name: "Burkina Faso" },
  BI: { code: "+257", length: 8, name: "Burundi" },
  KH: { code: "+855", length: 9, name: "Cambodia" },
  CM: { code: "+237", length: 9, name: "Cameroon" },
  CA: { code: "+1", length: 10, name: "Canada" },
  CV: { code: "+238", length: 7, name: "Cape Verde" },
  CF: { code: "+236", length: 8, name: "Central African Republic" },
  TD: { code: "+235", length: 8, name: "Chad" },
  CL: { code: "+56", length: 9, name: "Chile" },
  CN: { code: "+86", length: 11, name: "China" },
  CO: { code: "+57", length: 10, name: "Colombia" },
  KM: { code: "+269", length: 7, name: "Comoros" },
  CD: { code: "+243", length: 9, name: "Congo" },
  CR: { code: "+506", length: 8, name: "Costa Rica" },
  HR: { code: "+385", length: 9, name: "Croatia" },
  CU: { code: "+53", length: 8, name: "Cuba" },
  CY: { code: "+357", length: 8, name: "Cyprus" },
  CZ: { code: "+420", length: 9, name: "Czech Republic" },
  DK: { code: "+45", length: 8, name: "Denmark" },
  DJ: { code: "+253", length: 6, name: "Djibouti" },
  DM: { code: "+1", length: 10, name: "Dominica" },
  DO: { code: "+1", length: 10, name: "Dominican Republic" },
  EC: { code: "+593", length: 9, name: "Ecuador" },
  EG: { code: "+20", length: 10, name: "Egypt" },
  SV: { code: "+503", length: 8, name: "El Salvador" },
  GQ: { code: "+240", length: 9, name: "Equatorial Guinea" },
  ER: { code: "+291", length: 7, name: "Eritrea" },
  EE: { code: "+372", length: 7, name: "Estonia" },
  ET: { code: "+251", length: 9, name: "Ethiopia" },
  FJ: { code: "+679", length: 7, name: "Fiji" },
  FI: { code: "+358", length: 9, name: "Finland" },
  FR: { code: "+33", length: 9, name: "France" },
  GA: { code: "+241", length: 7, name: "Gabon" },
  GM: { code: "+220", length: 7, name: "Gambia" },
  GE: { code: "+995", length: 9, name: "Georgia" },
  DE: { code: "+49", length: 10, name: "Germany" },
  GH: { code: "+233", length: 9, name: "Ghana" },
  GR: { code: "+30", length: 10, name: "Greece" },
  GD: { code: "+1", length: 10, name: "Grenada" },
  GT: { code: "+502", length: 8, name: "Guatemala" },
  GN: { code: "+224", length: 9, name: "Guinea" },
  GW: { code: "+245", length: 7, name: "Guinea-Bissau" },
  GY: { code: "+592", length: 7, name: "Guyana" },
  HT: { code: "+509", length: 8, name: "Haiti" },
  HN: { code: "+504", length: 8, name: "Honduras" },
  HU: { code: "+36", length: 9, name: "Hungary" },
  IS: { code: "+354", length: 7, name: "Iceland" },
  IN: { code: "+91", length: 10, name: "India" },
  ID: { code: "+62", length: 10, name: "Indonesia" },
  IR: { code: "+98", length: 10, name: "Iran" },
  IQ: { code: "+964", length: 10, name: "Iraq" },
  IE: { code: "+353", length: 9, name: "Ireland" },
  IL: { code: "+972", length: 9, name: "Israel" },
  IT: { code: "+39", length: 10, name: "Italy" },
  JM: { code: "+1", length: 10, name: "Jamaica" },
  JP: { code: "+81", length: 10, name: "Japan" },
  JO: { code: "+962", length: 9, name: "Jordan" },
  KZ: { code: "+7", length: 10, name: "Kazakhstan" },
  KE: { code: "+254", length: 9, name: "Kenya" },
  KI: { code: "+686", length: 5, name: "Kiribati" },
  KP: { code: "+850", length: 8, name: "North Korea" },
  KR: { code: "+82", length: 10, name: "South Korea" },
  KW: { code: "+965", length: 8, name: "Kuwait" },
  KG: { code: "+996", length: 9, name: "Kyrgyzstan" },
  LA: { code: "+856", length: 9, name: "Laos" },
  LV: { code: "+371", length: 8, name: "Latvia" },
  LB: { code: "+961", length: 7, name: "Lebanon" },
  LS: { code: "+266", length: 8, name: "Lesotho" },
  LR: { code: "+231", length: 7, name: "Liberia" },
  LY: { code: "+218", length: 9, name: "Libya" },
  LI: { code: "+423", length: 7, name: "Liechtenstein" },
  LT: { code: "+370", length: 8, name: "Lithuania" },
  LU: { code: "+352", length: 9, name: "Luxembourg" },
  MK: { code: "+389", length: 8, name: "North Macedonia" },
  MG: { code: "+261", length: 9, name: "Madagascar" },
  MW: { code: "+265", length: 9, name: "Malawi" },
  MY: { code: "+60", length: 9, name: "Malaysia" },
  MV: { code: "+960", length: 7, name: "Maldives" },
  ML: { code: "+223", length: 8, name: "Mali" },
  MT: { code: "+356", length: 8, name: "Malta" },
  MH: { code: "+692", length: 7, name: "Marshall Islands" },
  MR: { code: "+222", length: 8, name: "Mauritania" },
  MU: { code: "+230", length: 7, name: "Mauritius" },
  MX: { code: "+52", length: 10, name: "Mexico" },
  FM: { code: "+691", length: 7, name: "Micronesia" },
  MD: { code: "+373", length: 8, name: "Moldova" },
  MC: { code: "+377", length: 8, name: "Monaco" },
  MN: { code: "+976", length: 8, name: "Mongolia" },
  ME: { code: "+382", length: 8, name: "Montenegro" },
  MA: { code: "+212", length: 9, name: "Morocco" },
  MZ: { code: "+258", length: 9, name: "Mozambique" },
  MM: { code: "+95", length: 8, name: "Myanmar" },
  NA: { code: "+264", length: 9, name: "Namibia" },
  NR: { code: "+674", length: 7, name: "Nauru" },
  NP: { code: "+977", length: 10, name: "Nepal" },
  NL: { code: "+31", length: 9, name: "Netherlands" },
  NZ: { code: "+64", length: 9, name: "New Zealand" },
  NI: { code: "+505", length: 8, name: "Nicaragua" },
  NE: { code: "+227", length: 8, name: "Niger" },
  NG: { code: "+234", length: 10, name: "Nigeria" },
  NO: { code: "+47", length: 8, name: "Norway" },
  OM: { code: "+968", length: 8, name: "Oman" },
  PK: { code: "+92", length: 10, name: "Pakistan" },
  PW: { code: "+680", length: 7, name: "Palau" },
  PS: { code: "+970", length: 9, name: "Palestine" },
  PA: { code: "+507", length: 8, name: "Panama" },
  PG: { code: "+675", length: 7, name: "Papua New Guinea" },
  PY: { code: "+595", length: 9, name: "Paraguay" },
  PE: { code: "+51", length: 9, name: "Peru" },
  PH: { code: "+63", length: 10, name: "Philippines" },
  PL: { code: "+48", length: 9, name: "Poland" },
  PT: { code: "+351", length: 9, name: "Portugal" },
  QA: { code: "+974", length: 8, name: "Qatar" },
  RO: { code: "+40", length: 9, name: "Romania" },
  RU: { code: "+7", length: 10, name: "Russia" },
  RW: { code: "+250", length: 9, name: "Rwanda" },
  KN: { code: "+1", length: 10, name: "Saint Kitts and Nevis" },
  LC: { code: "+1", length: 10, name: "Saint Lucia" },
  VC: { code: "+1", length: 10, name: "Saint Vincent" },
  WS: { code: "+685", length: 5, name: "Samoa" },
  SM: { code: "+378", length: 8, name: "San Marino" },
  ST: { code: "+239", length: 7, name: "Sao Tome and Principe" },
  SA: { code: "+966", length: 9, name: "Saudi Arabia" },
  SN: { code: "+221", length: 9, name: "Senegal" },
  RS: { code: "+381", length: 9, name: "Serbia" },
  SC: { code: "+248", length: 7, name: "Seychelles" },
  SL: { code: "+232", length: 8, name: "Sierra Leone" },
  SG: { code: "+65", length: 8, name: "Singapore" },
  SK: { code: "+421", length: 9, name: "Slovakia" },
  SI: { code: "+386", length: 8, name: "Slovenia" },
  SB: { code: "+677", length: 5, name: "Solomon Islands" },
  SO: { code: "+252", length: 7, name: "Somalia" },
  ZA: { code: "+27", length: 9, name: "South Africa" },
  SS: { code: "+211", length: 9, name: "South Sudan" },
  ES: { code: "+34", length: 9, name: "Spain" },
  LK: { code: "+94", length: 9, name: "Sri Lanka" },
  SD: { code: "+249", length: 9, name: "Sudan" },
  SR: { code: "+597", length: 7, name: "Suriname" },
  SZ: { code: "+268", length: 8, name: "Eswatini" },
  SE: { code: "+46", length: 9, name: "Sweden" },
  CH: { code: "+41", length: 9, name: "Switzerland" },
  SY: { code: "+963", length: 9, name: "Syria" },
  TW: { code: "+886", length: 9, name: "Taiwan" },
  TJ: { code: "+992", length: 9, name: "Tajikistan" },
  TZ: { code: "+255", length: 9, name: "Tanzania" },
  TH: { code: "+66", length: 9, name: "Thailand" },
  TL: { code: "+670", length: 7, name: "Timor-Leste" },
  TG: { code: "+228", length: 8, name: "Togo" },
  TO: { code: "+676", length: 5, name: "Tonga" },
  TT: { code: "+1", length: 10, name: "Trinidad and Tobago" },
  TN: { code: "+216", length: 8, name: "Tunisia" },
  TR: { code: "+90", length: 10, name: "Turkey" },
  TM: { code: "+993", length: 8, name: "Turkmenistan" },
  TV: { code: "+688", length: 5, name: "Tuvalu" },
  UG: { code: "+256", length: 9, name: "Uganda" },
  UA: { code: "+380", length: 9, name: "Ukraine" },
  AE: { code: "+971", length: 9, name: "United Arab Emirates" },
  GB: { code: "+44", length: 10, name: "United Kingdom" },
  US: { code: "+1", length: 10, name: "United States" },
  UY: { code: "+598", length: 8, name: "Uruguay" },
  UZ: { code: "+998", length: 9, name: "Uzbekistan" },
  VU: { code: "+678", length: 5, name: "Vanuatu" },
  VA: { code: "+39", length: 10, name: "Vatican City" },
  VE: { code: "+58", length: 10, name: "Venezuela" },
  VN: { code: "+84", length: 9, name: "Vietnam" },
  YE: { code: "+967", length: 9, name: "Yemen" },
  ZM: { code: "+260", length: 9, name: "Zambia" },
  ZW: { code: "+263", length: 9, name: "Zimbabwe" }
};

const languages = [
  { label: "English", value: "english", icon: "https://flagcdn.com/us.svg" },
  { label: "Dari", value: "dari", icon: "https://flagcdn.com/af.svg" },
  { label: "Pashto", value: "pashto", icon: "https://flagcdn.com/af.svg" },
];

const StyledDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiBackdrop-root": {
    backdropFilter: "blur(6px)",
    backgroundColor: "rgba(0, 0, 0, 0.2)",
  },
  "& .MuiPaper-root": {
    width: "90%",
    maxWidth: "100%",
    border: `1px solid ${theme.palette.mode === "dark" ? "#444" : "#d32f2f"}`,
    borderRadius: 12,
    padding: theme.spacing(2),
    backgroundColor:
      theme.palette.mode === "dark"
        ? "#111827"
        : theme.palette.background.paper,
    [theme.breakpoints.up("sm")]: { width: "90%" },
    [theme.breakpoints.up("md")]: { width: "600px" },
    [theme.breakpoints.up("lg")]: { width: "700px" },
  },
}));

const validatePhoneNumber = (number, countryCode, isAlternative = false) => {
  if (!countryCode) return "Please select a country first";

  const countryInfo = countryDialCodes[countryCode];
  if (!countryInfo) return "Invalid country selection";

  if (!number || !number.trim()) {
    return isAlternative ? "Alternative contact number is optional" : "Phone number is required";
  }

  const cleanNumber = number.replace(/\s+/g, "");

  if (!/^\d+$/.test(cleanNumber)) {
    return "Phone number must contain only digits";
  }

  if (cleanNumber.length !== countryInfo.length) {
    return `${countryInfo.name} phone numbers must be exactly ${countryInfo.length} digits after ${countryInfo.code}`;
  }

  return null;
};

export default function CreateAgent({ open, onClose, refetch }) {
  const userinfo = useSelector((state) => state.auth.user);
  const fileRef = useRef(null);
  const [isTransferSuccess, setIsTransferSuccess] = useState(false);
  const [isError, setIsError] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [errors, setErrors] = useState({});
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [localNumber, setLocalNumber] = useState("");
  const [altLocalNumber, setAltLocalNumber] = useState("");

  const [payload, setPayload] = useState({
    parentAgentId: userinfo?.uid,
    user_type: "agent",
    registrationType: "indirect",
    accountType: "retailer",
    alternativeContact: "",
    mobileNumber: "",
    username: "",
    email: "",
    country: "",
    province: "",
    district: "",
    address: "",
    messageLanguage: "",
    profile_picture: "",
    status: false,
  });

  useEffect(() => {
    if (!open) {
      resetForm();
      setSelectedCountry(null);
      setLocalNumber("");
      setAltLocalNumber("");
    }
  }, [open]);

  const resetForm = () => {
    setPayload({
      parentAgentId: userinfo?.uid,
      user_type: "agent",
      registrationType: "indirect",
      accountType: "retailer",
      alternativeContact: "",
      mobileNumber: "",
      username: "",
      email: "",
      country: "",
      province: "",
      district: "",
      address: "",
      messageLanguage: "",
      profile_picture: "",
      status: false,
    });
    setSelectedCountry(null);
    setLocalNumber("");
    setAltLocalNumber("");
  };

  const validate = () => {
    const newErrors = {};

    if (!payload.username) newErrors.username = "Agent name is required.";
    if (!payload.email) {
      newErrors.email = "Email is required.";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(payload.email)) {
      newErrors.email = "Invalid email format.";
    }

    if (!selectedCountry) {
      newErrors.mobileNumber = "Please select a country first.";
    } else {
      const phoneError = validatePhoneNumber(localNumber, selectedCountry.countryCode);
      if (phoneError) newErrors.mobileNumber = phoneError;
    }

    if (altLocalNumber.trim()) {
      const altError = validatePhoneNumber(altLocalNumber, selectedCountry?.countryCode, true);
      if (altError) newErrors.alternativeContact = altError;
    }

    if (!payload.country) newErrors.country = "Country is required.";
    if (!payload.province) newErrors.province = "Province is required.";
    if (!payload.district) newErrors.district = "District is required.";
    if (!payload.address) newErrors.address = "Address is required.";
    if (!payload.messageLanguage) newErrors.messageLanguage = "Language is required.";
    if (!payload.status) newErrors.status = "Status is required.";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const { data: countries, isLoading: loadingCountries } = useQuery({
    queryKey: ["countries"],
    queryFn: getCoutries,
  });

  const { data: provinces, isLoading: loadingProvinces } = useQuery({
    queryKey: ["provinces", payload?.country],
    queryFn: () => getProvincesBaseOnCountry(payload?.country),
    enabled: !!payload?.country,
  });

  const { data: districts, isLoading: loadingDistricts } = useQuery({
    queryKey: ["districts", payload?.province],
    queryFn: () => getDistrictsBaseOnProvince(payload?.province),
    enabled: !!payload?.province,
  });

  const { mutate, isPending: loading } = useMutation({
    mutationFn: (payload) => createDownlineAgent(payload),
    onSuccess: (response) => {
      toast.success("Agent Created Successfully");
      setIsTransferSuccess(true);
      resetForm();
      refetch();
    },
    onError: (error) => {
      console.log("create agent error:", error);
      const message = error?.response?.data?.error || "Failed to create agent";
      setIsError(true);
      toast.error(message);
      setErrorMessage(message);
    },
  });

  const statuses = [
    { label: "Active", value: "active" },
    { label: "Inactive", value: "inactive" },
  ];

  const handlePayloadChange = (field, value) => {
    setPayload((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => ({ ...prev, [field]: null }));
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (file) {
      try {
        const compressed = await imageCompression(file, {
          maxSizeMB: 0.5,
          maxWidthOrHeight: 300,
          useWebWorker: true,
        });

        const reader = new FileReader();
        reader.onload = () => handlePayloadChange("profile_picture", reader.result);
        reader.readAsDataURL(compressed);
      } catch (error) {
        console.error("Image compression error:", error);
        toast.error("Failed to process image");
      }
    }
  };

  const handleCountryChange = (event, newValue) => {
    if (newValue) {
      setSelectedCountry(newValue);
      handlePayloadChange("country", newValue.id);
      handlePayloadChange("province", "");
      handlePayloadChange("district", "");
      setLocalNumber("");
      setAltLocalNumber("");
      handlePayloadChange("mobileNumber", "");
      handlePayloadChange("alternativeContact", "");
    } else {
      setSelectedCountry(null);
      handlePayloadChange("country", "");
    }
  };

  const handlePhoneChange = (e) => {
    const raw = e.target.value;
    const maxLength = selectedCountry ? countryDialCodes[selectedCountry.countryCode]?.length : Infinity;

    if (/^[\d\s]*$/.test(raw)) {
      const digitsOnly = raw.replace(/\s+/g, "");

      if (digitsOnly.length <= maxLength) {
        setLocalNumber(raw);
        setErrors(prev => ({ ...prev, mobileNumber: null }));
      }
    }
  };

  const handleAltPhoneChange = (e) => {
    const raw = e.target.value;
    const maxLength = selectedCountry ? countryDialCodes[selectedCountry.countryCode]?.length : Infinity;

    if (/^[\d\s]*$/.test(raw)) {
      const digitsOnly = raw.replace(/\s+/g, "");

      if (digitsOnly.length <= maxLength) {
        setAltLocalNumber(raw);
        setErrors(prev => ({ ...prev, alternativeContact: null }));
      }
    }
  };

  useEffect(() => {
    if (selectedCountry && localNumber.trim()) {
      const countryInfo = countryDialCodes[selectedCountry.countryCode];
      const dialCode = countryInfo?.code || "";
      const cleanNumber = localNumber.replace(/\s+/g, "");
      const fullNumber = dialCode + cleanNumber;
      handlePayloadChange("mobileNumber", fullNumber);
    } else {
      handlePayloadChange("mobileNumber", "");
    }
  }, [selectedCountry, localNumber]);

  useEffect(() => {
    if (selectedCountry && altLocalNumber.trim()) {
      const countryInfo = countryDialCodes[selectedCountry.countryCode];
      const dialCode = countryInfo?.code || "";
      const cleanNumber = altLocalNumber.replace(/\s+/g, "");
      const fullNumber = dialCode + cleanNumber;
      handlePayloadChange("alternativeContact", fullNumber);
    } else {
      handlePayloadChange("alternativeContact", "");
    }
  }, [selectedCountry, altLocalNumber]);

  const onSubmit = async () => {
    if (!validate()) return;
    mutate(payload);
  };

  const getPhonePlaceholder = () => {
    if (!selectedCountry) return "Select country first";
    const countryInfo = countryDialCodes[selectedCountry.countryCode];
    if (!countryInfo || !countryInfo.length) return "Enter phone number";
    const example = 'X'.repeat(countryInfo.length);
    return `(e.g., ${example})`;
  };


  return (
    <StyledDialog open={open} onClose={onClose}>
      <CreateAgentSuccessModal
        open={isTransferSuccess}
        onClose={() => {
          setIsTransferSuccess(false);
          onClose();
        }}
        payload={payload}
      />
      <CreateAgentErrorModal
        open={isError}
        onClose={() => setIsError(false)}
        error={errorMessage}
        onRetry={onSubmit}
        payload={payload}
      />

      <div className="px-5 py-3">
        <h3 className="my-3 text-xl font-semibold">Create Agent</h3>
        <div className="flex justify-center">
          <div className="relative w-[120px] h-[120px] rounded-full border border-red-500 mb-4">
            <img
              src={payload.profile_picture || "/default-avatar.png"}
              alt="profile"
              className="w-full h-full rounded-full object-cover"
            />
            <div
              onClick={() => fileRef.current?.click()}
              className="absolute bottom-0 right-0 bg-white rounded-full p-1 cursor-pointer shadow"
            >
              <Camera size={20} />
              <input
                ref={fileRef}
                type="file"
                accept="image/*"
                hidden
                onChange={handleImageUpload}
              />
            </div>
          </div>
        </div>

        <div className="w-full grid grid-cols-12 gap-3">
          <div className="col-span-12 md:col-span-6">
            <TextField
              onChange={(e) => handlePayloadChange("username", e.target.value)}
              value={payload.username}
              sx={redFocusStyles}
              variant="outlined"
              label="Agent Name"
              error={!!errors.username}
              helperText={errors.username || ""}
              fullWidth
            />
          </div>

          <div className="col-span-12 md:col-span-6">
            <TextField
              onChange={(e) => handlePayloadChange("email", e.target.value)}
              value={payload.email}
              sx={redFocusStyles}
              variant="outlined"
              label="Email"
              type="email"
              error={!!errors.email}
              helperText={errors.email || ""}
              fullWidth
            />
          </div>

          <div className="col-span-12 md:col-span-6">
            <Autocomplete
              options={countries?.data || []}
              getOptionLabel={(u) => u?.countryName}
              value={selectedCountry}
              onChange={handleCountryChange}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Country"
                  sx={redFocusStyles}
                  error={!!errors.country}
                  helperText={errors.country || ""}
                  required
                  fullWidth
                />
              )}
            />
          </div>

          <div className="col-span-12 md:col-span-6">
            <TextField
              disabled={!selectedCountry}
              onChange={handlePhoneChange}
              value={localNumber}
              sx={redFocusStyles}
              variant="outlined"
              label="Phone Number"
              placeholder={getPhonePlaceholder()}
              error={!!errors.mobileNumber}
              fullWidth
              InputProps={{
                startAdornment: selectedCountry ? (
                  <span style={{ marginRight: 8, color: '#666', fontWeight: 500 }}>
                    {countryDialCodes[selectedCountry.countryCode]?.code}
                  </span>
                ) : null,
              }}
            />
          </div>

          <div className="col-span-12 md:col-span-6">
            <TextField
              disabled={!selectedCountry}
              onChange={handleAltPhoneChange}
              value={altLocalNumber}
              sx={redFocusStyles}
              variant="outlined"
              label="Alternative Contact (Optional)"
              placeholder={selectedCountry ? `Optional - ${getPhonePlaceholder()}` : "Select country first"}
              error={!!errors.alternativeContact}
              fullWidth
              InputProps={{
                startAdornment: selectedCountry && altLocalNumber ? (
                  <span style={{ marginRight: 8, color: '#666', fontWeight: 500 }}>
                    {countryDialCodes[selectedCountry.countryCode]?.code}
                  </span>
                ) : null,
              }}
            />
          </div>

          <div className="col-span-12 md:col-span-6">
            <Autocomplete
              options={provinces?.data || []}
              getOptionLabel={(u) => u?.provinceName}
              value={provinces?.data?.find(p => p.id === payload.province) || null}
              onChange={(e, newValue) =>
                handlePayloadChange("province", newValue?.id || "")
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Province"
                  sx={redFocusStyles}
                  error={!!errors.province}
                  helperText={errors.province || ""}
                  required
                  fullWidth
                />
              )}
            />
          </div>

          <div className="col-span-12 md:col-span-6">
            <Autocomplete
              options={districts?.data || []}
              getOptionLabel={(u) => u?.districtName}
              value={districts?.data?.find(d => d.id === payload.district) || null}
              onChange={(e, newValue) =>
                handlePayloadChange("district", newValue?.id || "")
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="District"
                  sx={redFocusStyles}
                  error={!!errors.district}
                  helperText={errors.district || ""}
                  required
                  fullWidth
                />
              )}
            />
          </div>

          <div className="col-span-12 md:col-span-6">
            <TextField
              onChange={(e) => handlePayloadChange("address", e.target.value)}
              value={payload.address}
              sx={redFocusStyles}
              variant="outlined"
              label="Full Address"
              error={!!errors.address}
              helperText={errors.address || ""}
              fullWidth
              multiline
              rows={1}
            />
          </div>

          <div className="col-span-12 md:col-span-6">
            <Autocomplete
              onChange={(e, newValue) => {
                handlePayloadChange("messageLanguage", newValue?.value || "");
              }}
              options={languages}
              getOptionLabel={(option) => option.label}
              value={languages.find(l => l.value === payload.messageLanguage) || null}
              renderOption={(props, option) => (
                <Box
                  component="li"
                  sx={{ display: "flex", alignItems: "center", gap: 1 }}
                  {...props}
                >
                  <Avatar
                    src={option?.icon}
                    alt={option?.label}
                    sx={{ width: 20, height: 20 }}
                  />
                  {option?.label}
                </Box>
              )}
              sx={redFocusStyles}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Language"
                  placeholder="Select Language"
                  error={!!errors.messageLanguage}
                  helperText={errors.messageLanguage || ""}
                  fullWidth
                />
              )}
            />
          </div>

          <div className="col-span-12 md:col-span-6">
            <Autocomplete
              options={statuses || []}
              getOptionLabel={(u) => u?.label}
              value={statuses.find(s => s.value === payload.status) || null}
              onChange={(e, newValue) =>
                handlePayloadChange("status", newValue?.value || "")
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Status"
                  sx={redFocusStyles}
                  error={!!errors.status}
                  helperText={errors.status || ""}
                  required
                  fullWidth
                />
              )}
            />
          </div>
        </div>

        <div className="w-full flex justify-end mt-6 gap-3">
          <button
            onClick={onClose}
            className="py-2 px-6 border border-gray-400 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
          >
            Close
          </button>
          <button
            onClick={onSubmit}
            disabled={loading}
            className="text-white bg-[#CD0C02] py-2 px-6 rounded-md hover:bg-[#a80101] transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {loading ? (
              <>
                <CircularProgress size={20} color="inherit" />
                <span>Creating...</span>
              </>
            ) : (
              "Submit"
            )}
          </button>
        </div>
      </div>
    </StyledDialog>
  );
}